import { JSX } from 'preact';
import clsx from 'clsx';
import ChevronLeftIcon from 'images/icons/ChevronLeft.svg';
import ChevronRightIcon from 'images/icons/ChevronRight.svg';

import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

import { DotsNavigation } from './components/DotsNavigation';
import { useSlider } from './utilities/use-slider';

import styles from './Slider.module.scss';

interface SliderProps {
  slidesCount: number;
  slidesPerView: number;
  heading: string;
  children: JSX.Element[];
  slideSelector: string;
  minSlideWidth: number;
  maxSlideWidth: number;
}

export function Slider({
  slidesCount,
  slidesPerView,
  heading,
  children,
  slideSelector,
  minSlideWidth,
  maxSlideWidth,
}: SliderProps) {
  const { utils } = useSettings();

  const {
    currentSlide,
    controlsVisible,
    goToSlide,
    dots,
    sliderRef,
    next,
    prev,
    hasNext,
    hasPrev,
    dotsVisible,
    containerRef,
  } = useSlider(
    slidesCount,
    slidesPerView,
    minSlideWidth,
    maxSlideWidth,
    20,
    slideSelector,
  );

  const dotsMarkup = dotsVisible && (
    <DotsNavigation
      dots={dots}
      selected={currentSlide}
      onClick={(index) => goToSlide && goToSlide(index)}
    />
  );

  const arrowsMarkup = !utils.isRtl ? (
    <>
      <Button
        plain
        accessibilityLabel="Previous slide"
        icon={<Icon source={ChevronLeftIcon} />}
        disabled={!hasPrev}
        onClick={prev}
      />
      <Button
        plain
        accessibilityLabel="Next slide"
        icon={<Icon source={ChevronRightIcon} />}
        disabled={!hasNext}
        onClick={next}
      />
    </>
  ) : (
    <>
      <Button
        plain
        accessibilityLabel="Next slide"
        icon={<Icon source={ChevronRightIcon} />}
        disabled={!hasPrev}
        onClick={prev}
      />
      <Button
        plain
        accessibilityLabel="Previous slide"
        icon={<Icon source={ChevronLeftIcon} />}
        disabled={!hasNext}
        onClick={next}
      />
    </>
  );

  const headingMarkup = heading.length > 0 && (
    <div className={styles.SliderHead}>
      <Stack>
        <Stack.Item fill>
          <Text as="h2" variation="strong">
            {heading}
          </Text>
        </Stack.Item>
        {controlsVisible && arrowsMarkup}
      </Stack>
    </div>
  );

  return (
    <>
      {headingMarkup}
      <Stack vertical spacing="extraLoose">
        <div ref={containerRef} className={styles.SliderWrapper}>
          <div
            ref={sliderRef}
            className={clsx(styles.Slider, utils.isRtl && styles.Rtl)}
          >
            {children}
          </div>
        </div>
        {dotsMarkup}
      </Stack>
    </>
  );
}
